// The UI: one Preact root over the game, rendering the top of the screen stack (decision 0046). Focus navigation
// for every input: arrow keys and the pad move between a screen's items (nav.ts), Enter and Space (native) or the
// pad's south button press, Escape or the pad's east button go back; touch and the mouse press items directly. When a
// screen opens, its `data-autofocus` item (or its first) takes focus; going back restores the item that opened the
// child. On a phone held upright a rotate prompt covers everything (layout.ts). The game owns its screens, strings,
// theme and when screens open; the stack is the only state here.
import { h, render, type ComponentChildren, type VNode } from "preact";
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type El = VNode<any>;
import { IconButton, Icon } from "./components";
import { firstFocus, focusables, moveFocus, type NavIntent } from "./nav";
import { createPadNav, type PadLike, type PadNavOptions } from "./pad";
import { needsRotatePrompt } from "./layout";
import { ScreenStack, type ScreenEntry, type StackChange } from "./stack";
import { DEFAULT_THEME, injectStyle, themeVars, type UiTheme } from "./style";
import { highContrastTheme } from "./a11y";

/** Format a string by id (the game's strings table). */
export type Translate = (id: string, params?: Record<string, string | number | boolean | Date | null | undefined>) => string;

/** What a screen's render function gets. */
export interface ScreenContext {
  ui: Ui;
  t: Translate;
  /** The screen's id and the params it was opened with. */
  id: string;
  params: unknown;
  /** The stack entry's key (unique per opening). */
  key: number;
  /** How deep it sits (1 = the root screen). */
  depth: number;
}

/** A screen: a function of its context, returning (usually) a {@link Screen}. */
export type ScreenRender = (ctx: ScreenContext) => El;

/** Options for {@link createUi}. */
export interface UiOptions {
  t: Translate;
  screens: Record<string, ScreenRender>;
  /** String ids for the UI's own labels: the back button and the rotate prompt. */
  labels: { back: string; rotate: string };
  /** Where the UI's root goes (default `document.body`). */
  container?: HTMLElement;
  theme?: Partial<UiTheme>;
  /** Is this a phone (for the rotate prompt)? Default: never. */
  phone?: () => boolean;
  /** Read the pads (default `navigator.getGamepads()`). */
  pads?: () => readonly (PadLike | null)[];
  padNav?: PadNavOptions;
  /** Called after every stack change, once the new screen is drawn. */
  onChange?: (c: StackChange, stack: readonly ScreenEntry[]) => void;
}

/** The UI. */
export interface Ui {
  readonly root: HTMLElement;
  readonly stack: ScreenStack;
  push(id: string, params?: unknown): void;
  pop(): void;
  replace(id: string, params?: unknown): void;
  /** Make `id` the only screen. */
  reset(id: string, params?: unknown): void;
  /** Close every screen. */
  clear(): void;
  /** What back does on the top screen: its own handler, else close it when it isn't the root. */
  back(): void;
  /** The top screen's id. */
  top(): string | null;
  /** The stack's ids, bottom first. */
  ids(): string[];
  /** Apply a navigation intent (what an arrow key or a pad press does). */
  nav(intent: NavIntent): void;
  /** Draw again (after a setting or a fault changed what screens show). */
  refresh(): void;
  /** Add (or replace) a screen after the UI is made (a system mounted later brings its own: the map). */
  addScreen(id: string, render: ScreenRender): void;
  /** How many times the UI has rendered (per-frame HUD values must not add to it: gate D2.1). */
  renders(): number;
  setTheme(t: Partial<UiTheme>): void;
  /** Text size as a scale of the base (1 = 16 px). */
  setTextScale(scale: number): void;
  /** High contrast: the theme's high-contrast variant (decision 0063), `data-contrast` on the root. */
  setContrast(high: boolean): void;
  /** The custom properties in force (theme, text scale), to put on other roots (a HUD or captions outside the UI). */
  vars(): Record<string, string>;
  setReduceMotion(on: boolean): void;
  /** Hold the UI's keyboard and pad handling (a rebinding capture takes the next press itself). */
  holdInput(on: boolean): void;
  /** Is the rotate prompt up? */
  rotatePrompt(): boolean;
  dispose(): void;
}

/** Register the top screen's back handler; set by {@link Screen} while it renders. */
let registerBack: ((fn: (() => void) | null) => void) | null = null;

/** A screen: a heading (its accessible name), an optional back button, and its content, on a layer. */
export function Screen(p: {
  ctx: ScreenContext;
  title: string;
  /** "panel" (default): a box; "splash": the title's open layout; "dock": a narrow box at the start edge over the
   *  game, the rest of the screen left to what lies under it (photo mode's camera surface). */
  variant?: "panel" | "splash" | "dock";
  /** Dim what's behind (a screen over play). */
  dim?: boolean;
  /** What back does here (default: close this screen when it isn't the root). */
  onBack?: () => void;
  /** Show the back button (default: when back does something). */
  showBack?: boolean;
  children?: ComponentChildren;
}): El {
  const { ctx } = p;
  registerBack?.(p.onBack ?? null);
  const canBack = !!p.onBack || ctx.depth > 1;
  const hid = `rpg-screen-${ctx.key}-title`;
  return h(
    "div",
    { class: "rpg-layer", "data-dim": p.dim ? "" : undefined, "data-variant": p.variant ?? "panel" },
    h(
      "section",
      { class: "rpg-screen", role: "dialog", "aria-modal": "true", "aria-labelledby": hid, "data-screen": ctx.id, "data-variant": p.variant ?? "panel" },
      h(
        "header",
        { class: "rpg-screen-head" },
        (p.showBack ?? canBack) ? h(IconButton, { icon: "back", label: ctx.t(uiLabels.back), onPress: () => ctx.ui.back(), action: "back" }) : null,
        h("h1", { id: hid }, p.title),
      ),
      p.children,
    ),
  );
}

// the labels of the UI in use (one UI per page in practice; set by createUi)
let uiLabels: UiOptions["labels"] = { back: "ui.back", rotate: "ui.rotate" };

const KEY_INTENT: Record<string, Exclude<NavIntent, "confirm">> = { ArrowUp: "up", ArrowDown: "down", ArrowLeft: "left", ArrowRight: "right", Escape: "back" };

/** Build the UI and mount its root. */
export function createUi(o: UiOptions): Ui {
  const doc = (o.container ?? document.body).ownerDocument;
  injectStyle(doc);
  uiLabels = o.labels;
  const root = doc.createElement("div");
  root.className = "rpg-ui";
  root.dataset.rpgUi = "";
  // the screens are the page's main content while they show (the 3D view is a canvas with no text)
  root.setAttribute("role", "main");
  (o.container ?? doc.body).appendChild(root);
  let theme: UiTheme = { ...DEFAULT_THEME, ...o.theme };
  let high = false;
  let textScale = 1;
  const applyTheme = (): void => {
    for (const [k, v] of Object.entries(themeVars(high ? highContrastTheme(theme) : theme))) root.style.setProperty(k, v);
  };
  applyTheme();

  const stack = new ScreenStack();
  const screens: Record<string, ScreenRender> = { ...o.screens };
  const backs = new Map<number, (() => void) | null>();
  const focusMemory = new Map<number, number>();
  let drawn: number | null = null;
  let renders = 0;
  let held = false;
  let rotate = false;
  const phone = o.phone ?? (() => false);
  const win = doc.defaultView!;

  const host = (): HTMLElement | null => root.querySelector<HTMLElement>(".rpg-screen-host");

  const draw = (): void => {
    rotate = needsRotatePrompt({ phone: phone(), width: win.innerWidth, height: win.innerHeight });
    const top = stack.top;
    const entries = stack.entries;
    let vnode: El | null = null;
    if (top) {
      const screen = screens[top.id];
      if (!screen) throw new Error(`ui: no screen "${top.id}"`);
      const ctx: ScreenContext = { ui, t: o.t, id: top.id, params: top.params, key: top.key, depth: entries.length };
      vnode = h("div", { key: top.key, class: "rpg-screen-host", style: "display: contents", inert: rotate || undefined }, screen(ctx));
    }
    const prompt = rotate
      ? h("div", { class: "rpg-rotate", role: "alert", "data-screen": "rotate" }, h(Icon, { name: "rotate" }), h("p", null, o.t(o.labels.rotate)))
      : null;
    // a Screen registers its back handler while Preact renders it (render is synchronous)
    if (top) {
      backs.set(top.key, null);
      registerBack = (fn) => backs.set(top.key, fn);
    }
    try {
      render(h("div", { style: "display: contents" }, vnode, prompt), root);
    } finally {
      registerBack = null;
    }
    renders++;
    if (top && drawn !== top.key) {
      drawn = top.key;
      const el = host();
      if (el) {
        const remembered = focusMemory.get(top.key);
        const items = focusables(el);
        const target = (remembered !== undefined ? items[remembered] : undefined) ?? firstFocus(el);
        target?.focus();
      }
    }
    if (!top) drawn = null;
  };

  /** Remember which item of the top screen has focus, before a child covers it. */
  const remember = (): void => {
    const top = stack.top;
    const el = host();
    if (!top || !el) return;
    const i = focusables(el).indexOf(doc.activeElement as HTMLElement);
    if (i >= 0) focusMemory.set(top.key, i);
  };
  const forget = (): void => {
    const keys = new Set(stack.entries.map((e) => e.key));
    for (const k of [...focusMemory.keys()]) if (!keys.has(k)) focusMemory.delete(k);
    for (const k of [...backs.keys()]) if (!keys.has(k)) backs.delete(k);
  };
  stack.onChange((c) => {
    forget();
    // a screen needs the pointer: while it is locked (play on a desktop), every click goes to the locked element
    if (stack.top && doc.pointerLockElement) doc.exitPointerLock();
    draw();
    syncLoop();
    o.onChange?.(c, stack.entries);
  });

  const nav = (intent: NavIntent): void => {
    if (held || rotate || !stack.top) return;
    const el = host();
    if (!el) return;
    if (intent === "back") return ui.back();
    if (intent === "confirm") {
      const a = doc.activeElement as HTMLElement | null;
      if (a && el.contains(a)) a.click();
      else firstFocus(el)?.focus();
      return;
    }
    moveFocus(el, intent);
  };

  const onKey = (e: KeyboardEvent): void => {
    if (held || !stack.top || e.defaultPrevented) return;
    const intent = KEY_INTENT[e.key];
    if (!intent) return;
    const el = host();
    if (!el || rotate) return;
    if (intent === "back") {
      // the press is the screen's: stopped here (the document's capture phase), so a game listening on the window
      // never reads it, even when going back resumes play inside this handler (Escape, the pause press, would
      // otherwise reach the game the moment the stack is empty and pause it again)
      e.preventDefault();
      e.stopPropagation();
      ui.back();
      return;
    }
    // a keyboard's left and right step a focused slider natively; up and down always move between items
    if (moveFocus(el, intent, { native: true })) e.preventDefault();
    else if (intent === "up" || intent === "down") e.preventDefault();
  };
  doc.addEventListener("keydown", onKey, true);
  // A press on a screen is the UI's, not the game's: stop pointer events at the root, so a game listening on the
  // body (or the window) neither reads a menu click as gameplay input nor captures the pointer away from the button
  // (pointer capture on the body would retarget the click off it).
  const stop = (e: Event): void => e.stopPropagation();
  const POINTER_EVENTS = ["pointerdown", "pointerup", "pointercancel", "pointermove", "wheel", "contextmenu"] as const;
  for (const type of POINTER_EVENTS) root.addEventListener(type, stop);
  const onResize = (): void => {
    const before = rotate;
    if (needsRotatePrompt({ phone: phone(), width: win.innerWidth, height: win.innerHeight }) !== before) draw();
  };
  win.addEventListener("resize", onResize);

  // the pad loop runs only while a screen is open
  const readPads = o.pads ?? (() => (typeof navigator !== "undefined" && navigator.getGamepads ? navigator.getGamepads() : []));
  const padNav = createPadNav(o.padNav);
  let raf = 0;
  const tick = (): void => {
    raf = 0;
    if (!stack.top) return;
    // a screen that uses the left stick itself (photo mode's free camera: `data-pad-stick="off"`) keeps it out of
    // navigation; the d-pad still moves focus
    const pads = readPads();
    const stickOff = root.querySelector('[data-pad-stick="off"]') !== null;
    for (const intent of padNav(stickOff ? pads.map((p) => p && { buttons: p.buttons, axes: [] }) : pads, win.performance.now())) nav(intent);
    raf = win.requestAnimationFrame(tick);
  };
  const syncLoop = (): void => {
    if (stack.top && !raf) raf = win.requestAnimationFrame(tick);
  };

  const ui: Ui = {
    root,
    stack,
    push: (id, params) => {
      remember();
      stack.push(id, params);
    },
    pop: () => void stack.pop(),
    replace: (id, params) => void stack.replace(id, params),
    reset: (id, params) => void stack.reset(id, params),
    clear: () => stack.clear(),
    back() {
      const top = stack.top;
      if (!top) return;
      const handler = backs.get(top.key);
      if (handler) handler();
      else if (stack.size > 1) stack.pop();
    },
    top: () => stack.top?.id ?? null,
    ids: () => stack.entries.map((e) => e.id),
    nav,
    refresh: draw,
    addScreen(id, render) {
      screens[id] = render;
      if (stack.top?.id === id) draw();
    },
    renders: () => renders,
    setTheme(t) {
      theme = { ...theme, ...t };
      applyTheme();
    },
    setTextScale: (s) => {
      textScale = s;
      root.style.setProperty("--rpg-text-scale", String(s));
    },
    setContrast(on) {
      high = on;
      root.toggleAttribute("data-contrast", on);
      applyTheme();
    },
    vars: () => ({ ...themeVars(high ? highContrastTheme(theme) : theme), "--rpg-text-scale": String(textScale) }),
    setReduceMotion: (on) => root.toggleAttribute("data-reduce-motion", on),
    holdInput: (on) => void (held = on),
    rotatePrompt: () => rotate,
    dispose() {
      doc.removeEventListener("keydown", onKey, true);
      for (const type of POINTER_EVENTS) root.removeEventListener(type, stop);
      win.removeEventListener("resize", onResize);
      if (raf) win.cancelAnimationFrame(raf);
      render(null, root);
      root.remove();
    },
  };
  draw();
  return ui;
}
